import { memo } from "react";
import ReactMarkdown from 'react-markdown';
import { ADVISOR_COLORS } from '../../lib/advisorColors';

/**
 * Render markdown content using ReactMarkdown with advisor name color-coding.
 * @param {object} props
 * @param {string} props.content
 * @param {Array} props.advisors - Array of advisor objects with color information
 * @param {number} props.paragraphSpacing - Spacing between paragraphs (default: 0.25)
 */
export const MemoizedMarkdownMessage = memo(({ content, advisors = [], paragraphSpacing = 0.25 }) => {
  // Parse and render advisor names with colors
  const processContent = (text) => {
    const parts = text.split(/(\[ADVISOR:\s*[^\]]+\])/);
    let fallbackColorIndex = 0;
    
    return parts.map((part, index) => {
      const advisorMatch = part.match(/\[ADVISOR:\s*([^\]]+)\]/);
      if (advisorMatch) {
        const advisorName = advisorMatch[1].trim();
        // Look up advisor in advisors array to get their specific color
        const advisor = advisors.find(a => a.name.toLowerCase() === advisorName.toLowerCase());
        const colorClass = advisor?.color || ADVISOR_COLORS[fallbackColorIndex % ADVISOR_COLORS.length];
        fallbackColorIndex++;
        return (
          <h3 key={`advisor-${index}-${advisorName}`} className="font-bold font-serif text-lg mb-3 mt-6 text-gray-800 dark:text-term-100 flex items-center">
            <span className={`w-2 h-2 rounded-full ${colorClass} mr-3`}></span>
            {advisorName}
          </h3>
        );
      }
      return part;
    }).filter(part => typeof part === 'string' ? part.trim() : true);
  };

  // If content has advisor markers, render with special processing
  if (content.includes('[ADVISOR:')) {
    const processedParts = processContent(content);
    return (
      <div className="text-left font-serif w-full">
                {processedParts.map((part, index) => 
          typeof part === 'string' ? (
            <ReactMarkdown
              key={`md-${index}-${part.slice(0, 20)}`}
               className="text-left font-serif w-full"
               components={{
                 h1: ({ children }) => <h1 className="text-blue-600 dark:text-blue-400 font-bold font-serif text-xl">{children}</h1>,
                 h2: ({ children }) => <h2 className="text-term-600 dark:text-term-400 font-bold font-serif text-lg">{children}</h2>,
                 code: ({ node, inline, className, children, ...props }) => {
                   const match = /language-(\w+)/.exec(className || '');
                   return !inline ? (
                     <pre className="bg-stone-200 dark:bg-stone-900 p-4 rounded-md my-2 overflow-x-auto whitespace-pre-wrap break-all w-full">
                       <code className={`${match ? `language-${match[1]}` : ''} font-mono block text-sm text-gray-800 dark:text-term-100`} {...props}>
                         {children}
                       </code>
                     </pre>
                   ) : (
                     <code className="text-term-600 dark:text-term-400 font-mono text-sm bg-stone-200 dark:bg-stone-900 px-1 rounded" {...props}>
                       {children}
                     </code>
                   );
                 },
                 p: ({ children }) => <p className="font-serif w-full text-lg text-gray-800 dark:text-term-100" style={{ marginBottom: `${paragraphSpacing}rem`, lineHeight: '1.7' }}>{children}</p>,
                 ul: ({ children }) => <ul className="list-disc pl-4 space-y-1 w-full text-lg text-gray-800 dark:text-term-100" style={{ marginBottom: `${paragraphSpacing}rem` }}>{children}</ul>,
                 li: ({ children }) => <li className="text-lg text-gray-800 dark:text-term-100">{children}</li>,
               }}
             >
               {part.replace(/\n\n+/g, '\n\n')}
             </ReactMarkdown>
          ) : part
        )}
      </div>
    );
  }

  // Regular content without advisor markers - use paragraph spacing setting
  const processedContent = content.replace(/\n\n+/g, '\n\n');
  
  return (
    <ReactMarkdown
      className="text-left font-serif w-full"
      components={{
        h1: ({ children }) => <h1 className="text-blue-600 dark:text-blue-400 font-bold font-serif text-xl">{children}</h1>,
        h2: ({ children }) => <h2 className="text-term-600 dark:text-term-400 font-bold font-serif text-lg">{children}</h2>,
        code: ({ node, inline, className, children, ...props }) => {
          const match = /language-(\w+)/.exec(className || '');
          return !inline ? (
            <pre className="bg-stone-200 dark:bg-stone-900 p-4 rounded-md my-2 overflow-x-auto whitespace-pre-wrap break-all w-full">
              <code className={`${match ? `language-${match[1]}` : ''} font-mono block text-sm text-gray-800 dark:text-term-100`} {...props}>
                {children}
              </code>
            </pre>
          ) : (
            <code className="text-term-600 dark:text-term-400 font-mono text-sm bg-stone-200 dark:bg-stone-900 px-1 rounded" {...props}>
              {children}
            </code>
          );
        },
        p: ({ children }) => <p className="font-serif w-full text-lg text-gray-800 dark:text-term-100" style={{ marginBottom: `${paragraphSpacing}rem`, lineHeight: '1.7' }}>{children}</p>,
        ul: ({ children }) => <ul className="list-disc pl-4 space-y-1 w-full text-lg text-gray-800 dark:text-term-100" style={{ marginBottom: `${paragraphSpacing}rem` }}>{children}</ul>,
        li: ({ children }) => <li className="text-lg text-gray-800 dark:text-term-100">{children}</li>,
      }}
    >
      {processedContent}
    </ReactMarkdown>
  );
});

export default MemoizedMarkdownMessage;
